import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { cases } from '../../mocks/cases';
import CaseDetailSEOHead from './CaseDetailSEOHead';
import { useLang } from '../../hooks/useLang';
import { LangProvider } from '../../hooks/useLang';

const TYPE_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  债券:       { bg: 'bg-violet-50',  text: 'text-violet-700',  border: 'border-violet-200' },
  房地产:     { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200' },
  大宗商品:   { bg: 'bg-amber-50',   text: 'text-amber-700',   border: 'border-amber-200' },
  股权:       { bg: 'bg-pink-50',    text: 'text-pink-700',    border: 'border-pink-200' },
  碳信用:     { bg: 'bg-teal-50',    text: 'text-teal-700',    border: 'border-teal-200' },
  供应链信贷: { bg: 'bg-orange-50',  text: 'text-orange-700',  border: 'border-orange-200' },
  其他:       { bg: 'bg-slate-50',   text: 'text-slate-600',   border: 'border-slate-200' },
};

const CASE_DETAIL_LABELS: Record<string, Record<string, string>> = {
  home: { zh: '首页', en: 'Home', fr: 'Accueil', es: 'Inicio', pt: 'Início', ja: 'ホーム', de: 'Startseite', ko: '홈', ar: 'الرئيسية', ru: 'Главная' },
  caseLib: { zh: '案例深度库', en: 'Case Library', fr: 'Bibliothèque de Cas', es: 'Biblioteca de Casos', pt: 'Biblioteca de Casos', ja: '事例ライブラリ', de: 'Fallbibliothek', ko: '사례 라이브러리', ar: 'مكتبة الحالات', ru: 'Библиотека случаев' },
  notFound: { zh: '案例不存在', en: 'Case Not Found', fr: 'Cas introuvable', es: 'Caso no encontrado', pt: 'Caso não encontrado', ja: '事例が見つかりません', de: 'Fall nicht gefunden', ko: '사례를 찾을 수 없습니다', ar: 'الحالة غير موجودة', ru: 'Случай не найден' },
  backToLib: { zh: '← 返回案例库', en: '← Back to Library', fr: '← Retour à la bibliothèque', es: '← Volver a la biblioteca', pt: '← Voltar à biblioteca', ja: '← ライブラリに戻る', de: '← Zurück zur Bibliothek', ko: '← 라이브러리로 돌아가기', ar: '← العودة إلى المكتبة', ru: '← Назад в библиотеку' },
  hasContract: { zh: '含智能合约源码', en: 'Includes Smart Contract', fr: 'Inclut le contrat intelligent', es: 'Incluye contrato inteligente', pt: 'Inclui contrato inteligente', ja: 'スマートコントラクト含む', de: 'Enthält Smart Contract', ko: '스마트 계약 포함', ar: 'يتضمن عقداً ذكياً', ru: 'Включает смарт-контракт' },
  tabOverview: { zh: '案例概览', en: 'Overview', fr: 'Aperçu', es: 'Resumen', pt: 'Visão geral', ja: '概要', de: 'Übersicht', ko: '개요', ar: 'نظرة عامة', ru: 'Обзор' },
  tabContract: { zh: '智能合约源码', en: 'Smart Contract', fr: 'Contrat Intelligent', es: 'Contrato Inteligente', pt: 'Contrato Inteligente', ja: 'スマートコントラクト', de: 'Smart Contract', ko: '스마트 계약', ar: 'العقد الذكي', ru: 'Смарт-контракт' },
  highlights: { zh: '核心亮点', en: 'Key Highlights', fr: 'Points Clés', es: 'Puntos Clave', pt: 'Destaques Principais', ja: '主要ハイライト', de: 'Wichtige Highlights', ko: '핵심 하이라이트', ar: 'النقاط الرئيسية', ru: 'Ключевые моменты' },
  projectDetails: { zh: '项目详情', en: 'Project Details', fr: 'Détails du Projet', es: 'Detalles del Proyecto', pt: 'Detalhes do Projeto', ja: 'プロジェクト詳細', de: 'Projektdetails', ko: '프로젝트 세부 정보', ar: 'تفاصيل المشروع', ru: 'Детали проекта' },
  issuer: { zh: '发行机构', en: 'Issuer', fr: 'Émetteur', es: 'Emisor', pt: 'Emissor', ja: '発行機関', de: 'Emittent', ko: '발행 기관', ar: 'الجهة المصدرة', ru: 'Эмитент' },
  launchDate: { zh: '发行时间', en: 'Launch Date', fr: 'Date de lancement', es: 'Fecha de lanzamiento', pt: 'Data de lançamento', ja: '発行日', de: 'Startdatum', ko: '출시일', ar: 'تاريخ الإطلاق', ru: 'Дата запуска' },
  platform: { zh: '区块链平台', en: 'Blockchain Platform', fr: 'Plateforme Blockchain', es: 'Plataforma Blockchain', pt: 'Plataforma Blockchain', ja: 'ブロックチェーンプラットフォーム', de: 'Blockchain-Plattform', ko: '블록체인 플랫폼', ar: 'منصة البلوكتشين', ru: 'Блокчейн-платформа' },
  standard: { zh: '代币标准', en: 'Token Standard', fr: 'Standard de Token', es: 'Estándar de Token', pt: 'Padrão de Token', ja: 'トークン標準', de: 'Token-Standard', ko: '토큰 표준', ar: 'معيار الرمز', ru: 'Стандарт токена' },
  tvl: { zh: '规模/TVL', en: 'Scale/TVL', fr: 'Échelle/TVL', es: 'Escala/TVL', pt: 'Escala/TVL', ja: '規模/TVL', de: 'Umfang/TVL', ko: '규모/TVL', ar: 'الحجم/TVL', ru: 'Масштаб/TVL' },
  region: { zh: '发行地区', en: 'Region', fr: 'Région', es: 'Región', pt: 'Região', ja: '発行地域', de: 'Region', ko: '발행 지역', ar: 'المنطقة', ru: 'Регион' },
  contractAddress: { zh: '合约地址（Ethereum）', en: 'Contract Address (Ethereum)', fr: 'Adresse du contrat (Ethereum)', es: 'Dirección del contrato (Ethereum)', pt: 'Endereço do contrato (Ethereum)', ja: 'コントラクトアドレス（Ethereum）', de: 'Vertragsadresse (Ethereum)', ko: '계약 주소 (Ethereum)', ar: 'عنوان العقد (Ethereum)', ru: 'Адрес контракта (Ethereum)' },
  viewOnEtherscan: { zh: 'Etherscan →', en: 'Etherscan →', fr: 'Etherscan →', es: 'Etherscan →', pt: 'Etherscan →', ja: 'Etherscan →', de: 'Etherscan →', ko: 'Etherscan →', ar: 'Etherscan →', ru: 'Etherscan →' },
  contractSourceCode: { zh: '智能合约源码', en: 'Smart Contract Source Code', fr: 'Code Source du Contrat', es: 'Código Fuente del Contrato', pt: 'Código Fonte do Contrato', ja: 'スマートコントラクトソースコード', de: 'Smart-Contract-Quellcode', ko: '스마트 계약 소스 코드', ar: 'كود مصدر العقد الذكي', ru: 'Исходный код смарт-контракта' },
  viewOnEtherscanFull: { zh: '在Etherscan查看', en: 'View on Etherscan', fr: 'Voir sur Etherscan', es: 'Ver en Etherscan', pt: 'Ver no Etherscan', ja: 'Etherscanで見る', de: 'Auf Etherscan ansehen', ko: 'Etherscan에서 보기', ar: 'عرض على Etherscan', ru: 'Просмотр на Etherscan' },
  copyCode: { zh: '复制代码', en: 'Copy Code', fr: 'Copier le code', es: 'Copiar código', pt: 'Copiar código', ja: 'コードをコピー', de: 'Code kopieren', ko: '코드 복사', ar: 'نسخ الكود', ru: 'Копировать код' },
  copied: { zh: '已复制', en: 'Copied!', fr: 'Copié!', es: '¡Copiado!', pt: 'Copiado!', ja: 'コピー済み', de: 'Kopiert!', ko: '복사됨!', ar: 'تم النسخ!', ru: 'Скопировано!' },
  contractNotice: { zh: '以上源码为教学示例版本，真实部署合约可在Etherscan中查看完整已验证版本', en: 'The above source code is for educational purposes. The actual deployed contract can be viewed on Etherscan.', fr: 'Le code source ci-dessus est à des fins éducatives. Le contrat déployé peut être consulté sur Etherscan.', es: 'El código fuente anterior es para fines educativos. El contrato desplegado se puede ver en Etherscan.', pt: 'O código fonte acima é para fins educacionais. O contrato implantado pode ser visualizado no Etherscan.', ja: '上記のソースコードは教育目的のサンプルです。実際のデプロイ済みコントラクトはEtherscanで確認できます。', de: 'Der obige Quellcode dient zu Bildungszwecken. Den tatsächlich eingesetzten Vertrag finden Sie auf Etherscan.', ko: '위 소스 코드는 교육 목적의 예시입니다. 실제 배포된 계약은 Etherscan에서 확인할 수 있습니다.', ar: 'الكود المصدري أعلاه لأغراض تعليمية. يمكن عرض العقد المنشور الفعلي على Etherscan.', ru: 'Приведенный исходный код предназначен для образовательных целей. Фактически развернутый контракт можно просмотреть на Etherscan.' },
  keyData: { zh: '案例关键数据', en: 'Key Data', fr: 'Données Clés', es: 'Datos Clave', pt: 'Dados Principais', ja: '主要データ', de: 'Schlüsseldaten', ko: '핵심 데이터', ar: 'البيانات الرئيسية', ru: 'Ключевые данные' },
  status: { zh: '状态', en: 'Status', fr: 'Statut', es: 'Estado', pt: 'Status', ja: 'ステータス', de: 'Status', ko: '상태', ar: 'الحالة', ru: 'Статус' },
  location: { zh: '所在地区', en: 'Location', fr: 'Emplacement', es: 'Ubicación', pt: 'Localização', ja: '所在地域', de: 'Standort', ko: '위치', ar: 'الموقع', ru: 'Местоположение' },
  runningPlatform: { zh: '运行平台', en: 'Platform', fr: 'Plateforme', es: 'Plataforma', pt: 'Plataforma', ja: '実行プラットフォーム', de: 'Plattform', ko: '실행 플랫폼', ar: 'منصة التشغيل', ru: 'Платформа' },
  track: { zh: '所属赛道', en: 'Track', fr: 'Piste', es: 'Pista', pt: 'Trilha', ja: 'トラック', de: 'Track', ko: '트랙', ar: 'المسار', ru: 'Трек' },
  relatedCases: { zh: '同类推荐案例', en: 'Related Cases', fr: 'Cas Similaires', es: 'Casos Relacionados', pt: 'Casos Relacionados', ja: '関連事例', de: 'Ähnliche Fälle', ko: '관련 사례', ar: 'حالات مشابهة', ru: 'Похожие случаи' },
  viewAllType: { zh: '查看全部', en: 'View all', fr: 'Voir tout', es: 'Ver todo', pt: 'Ver tudo', ja: 'すべて見る', de: 'Alle anzeigen', ko: '모두 보기', ar: 'عرض الكل', ru: 'Посмотреть все' },
  cases: { zh: '案例', en: 'cases', fr: 'cas', es: 'casos', pt: 'casos', ja: '事例', de: 'Fälle', ko: '사례', ar: 'حالات', ru: 'случаев' },
  backToLibBtn: { zh: '返回案例库', en: 'Back to Library', fr: 'Retour à la bibliothèque', es: 'Volver a la biblioteca', pt: 'Voltar à biblioteca', ja: 'ライブラリに戻る', de: 'Zurück zur Bibliothek', ko: '라이브러리로 돌아가기', ar: 'العودة إلى المكتبة', ru: 'Назад в библиотеку' },
  ercStandard: { zh: 'ERC标准', en: 'ERC Standard', fr: 'Standard ERC', es: 'Estándar ERC', pt: 'Padrão ERC', ja: 'ERC標準', de: 'ERC-Standard', ko: 'ERC 표준', ar: 'معيار ERC', ru: 'Стандарт ERC' },
  qrDemo: { zh: '二维码演示', en: 'QR Demo', fr: 'Démo QR', es: 'Demo QR', pt: 'Demo QR', ja: 'QRデモ', de: 'QR-Demo', ko: 'QR 데모', ar: 'عرض QR', ru: 'QR-демо' },
};

function CaseDetailContent() {
  const { id } = useParams<{ id: string }>();
  const { lang } = useLang();
  const caseItem = cases.find((c) => c.id === Number(id));
  const [codeCopied, setCodeCopied] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'contract'>('overview');

  const getLabel = (key: string) => CASE_DETAIL_LABELS[key]?.[lang] ?? CASE_DETAIL_LABELS[key]?.['en'] ?? key;

  const TYPE_TRANSLATIONS: Record<string, Record<string, string>> = {
    '债券': { en: 'Bonds', fr: 'Obligations', es: 'Bonos', pt: 'Títulos', ja: '債券', de: 'Anleihen', ko: '채권', ar: 'سندات', ru: 'Облигации' },
    '房地产': { en: 'Real Estate', fr: 'Immobilier', es: 'Bienes Raíces', pt: 'Imóveis', ja: '不動産', de: 'Immobilien', ko: '부동산', ar: 'عقارات', ru: 'Недвижимость' },
    '大宗商品': { en: 'Commodities', fr: 'Matières Premières', es: 'Materias Primas', pt: 'Commodities', ja: '商品', de: 'Rohstoffe', ko: '원자재', ar: 'سلع', ru: 'Сырьё' },
    '股权': { en: 'Equity', fr: 'Actions', es: 'Acciones', pt: 'Ações', ja: '株式', de: 'Aktien', ko: '주식', ar: 'أسهم', ru: 'Акции' },
    '碳信用': { en: 'Carbon Credits', fr: 'Crédits Carbone', es: 'Créditos de Carbono', pt: 'Créditos de Carbono', ja: 'カーボンクレジット', de: 'CO₂-Zertifikate', ko: '탄소 크레딧', ar: 'ائتمانات الكربون', ru: 'Углеродные кредиты' },
    '供应链信贷': { en: 'Supply Chain Credit', fr: 'Crédit Chaîne d\'Approvisionnement', es: 'Crédito de Cadena de Suministro', pt: 'Crédito de Cadeia de Suprimentos', ja: 'サプライチェーン信用', de: 'Lieferketten-Kredit', ko: '공급망 신용', ar: 'ائتمان سلسلة التوريد', ru: 'Кредит цепочки поставок' },
    '其他': { en: 'Other', fr: 'Autre', es: 'Otro', pt: 'Outro', ja: 'その他', de: 'Sonstige', ko: '기타', ar: 'أخرى', ru: 'Другое' },
  };

  const STATUS_TRANSLATIONS: Record<string, Record<string, string>> = {
    '运营中': { en: 'Active', fr: 'Actif', es: 'Activo', pt: 'Ativo', ja: '運営中', de: 'Aktiv', ko: '운영 중', ar: 'نشط', ru: 'Активен' },
    '试点': { en: 'Pilot', fr: 'Pilote', es: 'Piloto', pt: 'Piloto', ja: 'パイロット', de: 'Pilotprojekt', ko: '시범', ar: 'تجريبي', ru: 'Пилот' },
    '已发行': { en: 'Issued', fr: 'Émis', es: 'Emitido', pt: 'Emitido', ja: '発行済み', de: 'Ausgegeben', ko: '발행됨', ar: 'صادر', ru: 'Выпущен' },
    '规划中': { en: 'Planned', fr: 'Planifié', es: 'Planificado', pt: 'Planejado', ja: '計画中', de: 'Geplant', ko: '계획 중', ar: 'مخطط', ru: 'Запланирован' },
  };

  const getTypeLabel = (type: string) => lang === 'zh' ? type : (TYPE_TRANSLATIONS[type]?.[lang] ?? TYPE_TRANSLATIONS[type]?.['en'] ?? type);
  const getStatusLabel = (status: string) => lang === 'zh' ? status : (STATUS_TRANSLATIONS[status]?.[lang] ?? STATUS_TRANSLATIONS[status]?.['en'] ?? status);

  if (!caseItem) {
    return (
      <div className="min-h-screen bg-[#F8F7FF] flex items-center justify-center">
        <div className="text-center">
          <i className="ri-error-warning-line text-4xl text-gray-300 block mb-3"></i>
          <h2 className="text-xl font-bold text-gray-700 mb-2">{getLabel('notFound')}</h2>
          <Link to="/cases" className="text-[#7C3AED] hover:underline text-sm cursor-pointer">{getLabel('backToLib')}</Link>
        </div>
      </div>
    );
  }

  const tc = TYPE_COLORS[caseItem.type] || TYPE_COLORS['其他'];
  const relatedCases = cases.filter((c) => c.type === caseItem.type && c.id !== caseItem.id).slice(0, 3);

  const handleCopyCode = () => {
    if (caseItem.contractCode) {
      navigator.clipboard.writeText(caseItem.contractCode);
      setCodeCopied(true);
      setTimeout(() => setCodeCopied(false), 2000);
    }
  };

  const metaFields = [
    { labelKey: 'issuer', value: caseItem.issuer, icon: 'ri-building-2-line' },
    { labelKey: 'launchDate', value: caseItem.launchDate, icon: 'ri-calendar-line' },
    { labelKey: 'platform', value: caseItem.platform, icon: 'ri-links-line' },
    { labelKey: 'standard', value: caseItem.standard, icon: 'ri-code-box-line' },
    { labelKey: 'tvl', value: caseItem.tvl, icon: 'ri-bar-chart-2-line' },
    { labelKey: 'region', value: caseItem.region, icon: 'ri-map-pin-line' },
  ];

  const sidebarStats = [
    { labelKey: 'tvl', value: caseItem.tvl, icon: 'ri-coins-line', color: 'text-[#7C3AED]' },
    { labelKey: 'status', value: getStatusLabel(caseItem.status), icon: 'ri-pulse-line', color: 'text-emerald-600' },
    { labelKey: 'location', value: caseItem.region, icon: 'ri-map-pin-2-line', color: 'text-[#F59E0B]' },
    { labelKey: 'runningPlatform', value: caseItem.platform, icon: 'ri-server-line', color: 'text-[#3B82F6]' },
    { labelKey: 'track', value: caseItem.track, icon: 'ri-medal-line', color: 'text-[#F97316]' },
  ];

  return (
    <div className="min-h-screen bg-[#F8F7FF]">
      <CaseDetailSEOHead caseItem={caseItem} />
      {/* Top nav */}
      <div className="bg-white border-b border-gray-100 px-4 lg:px-8 py-3">
        <div className="max-w-[1440px] mx-auto flex items-center gap-2 text-sm">
          <Link to="/" className="text-gray-500 hover:text-[#7C3AED] cursor-pointer flex items-center gap-1 transition-colors">
            <i className="ri-home-line text-xs"></i> {getLabel('home')}
          </Link>
          <i className="ri-arrow-right-s-line text-gray-300 text-xs"></i>
          <Link to="/cases" className="text-gray-500 hover:text-[#7C3AED] cursor-pointer transition-colors">{getLabel('caseLib')}</Link>
          <i className="ri-arrow-right-s-line text-gray-300 text-xs"></i>
          <span className="text-gray-700 font-medium truncate max-w-xs">{caseItem.name}</span>
        </div>
      </div>

      <div className="max-w-[1440px] mx-auto px-4 lg:px-8 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main content */}
          <div className="lg:col-span-2">
            {/* Header card */}
            <div className="bg-white rounded-2xl border border-gray-100 p-6 mb-6">
              <div className="flex items-start gap-3 mb-4">
                <div className="flex flex-wrap gap-2">
                  <span className={`text-xs px-2.5 py-1 rounded-full font-semibold border ${tc.bg} ${tc.text} ${tc.border}`}>{getTypeLabel(caseItem.type)}</span>
                  <span className="text-xs px-2.5 py-1 rounded-full font-semibold bg-[#7C3AED]/8 text-[#7C3AED] border border-[#7C3AED]/15">{caseItem.track}</span>
                  {caseItem.contractCode && (
                    <span className="text-xs px-2.5 py-1 rounded-full font-semibold bg-[#1D4ED8]/8 text-[#1D4ED8] border border-[#1D4ED8]/15 flex items-center gap-1">
                      <i className="ri-code-s-slash-line"></i>{getLabel('hasContract')}
                    </span>
                  )}
                </div>
              </div>
              <h1 className="text-2xl lg:text-3xl font-black text-gray-900 mb-3">{caseItem.name}</h1>
              <p className="text-gray-600 leading-relaxed text-base">{caseItem.detail || caseItem.desc}</p>
            </div>

            {/* Tabs */}
            {caseItem.contractCode && (
              <div className="flex gap-1 mb-4 bg-white border border-gray-100 rounded-xl p-1 w-fit">
                <button onClick={() => setActiveTab('overview')}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${activeTab === 'overview' ? 'bg-[#7C3AED] text-white' : 'text-gray-600 hover:text-[#7C3AED]'}`}>
                  <i className="ri-information-line mr-1.5"></i>{getLabel('tabOverview')}
                </button>
                <button onClick={() => setActiveTab('contract')}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${activeTab === 'contract' ? 'bg-[#7C3AED] text-white' : 'text-gray-600 hover:text-[#7C3AED]'}`}>
                  <i className="ri-code-s-slash-line mr-1.5"></i>{getLabel('tabContract')}
                </button>
              </div>
            )}

            {/* Tab: Overview */}
            {(activeTab === 'overview' || !caseItem.contractCode) && (
              <div className="space-y-4">
                {/* Highlights */}
                {caseItem.highlights && caseItem.highlights.length > 0 && (
                  <div className="bg-white rounded-2xl border border-gray-100 p-6">
                    <h3 className="font-black text-gray-900 text-base mb-4 flex items-center gap-2">
                      <div className="w-6 h-6 flex items-center justify-center bg-[#7C3AED]/10 rounded-lg">
                        <i className="ri-star-line text-[#7C3AED] text-sm"></i>
                      </div>
                      {getLabel('highlights')}
                    </h3>
                    <div className="grid sm:grid-cols-2 gap-3">
                      {caseItem.highlights.map((h, i) => (
                        <div key={i} className="flex items-start gap-2.5 bg-[#F8F7FF] rounded-xl p-3">
                          <div className="w-5 h-5 flex items-center justify-center bg-[#7C3AED] rounded-full shrink-0 mt-0.5">
                            <i className="ri-check-line text-white text-xs"></i>
                          </div>
                          <p className="text-gray-700 text-sm leading-relaxed">{h}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Meta info */}
                <div className="bg-white rounded-2xl border border-gray-100 p-6">
                  <h3 className="font-black text-gray-900 text-base mb-4 flex items-center gap-2">
                    <div className="w-6 h-6 flex items-center justify-center bg-[#7C3AED]/10 rounded-lg">
                      <i className="ri-file-list-3-line text-[#7C3AED] text-sm"></i>
                    </div>
                    {getLabel('projectDetails')}
                  </h3>
                  <div className="grid sm:grid-cols-2 gap-4">
                    {metaFields.filter((item) => item.value).map((item) => (
                      <div key={item.labelKey} className="flex items-center gap-3">
                        <div className="w-8 h-8 flex items-center justify-center bg-gray-50 rounded-lg border border-gray-100 shrink-0">
                          <i className={`${item.icon} text-gray-500 text-sm`}></i>
                        </div>
                        <div>
                          <div className="text-xs text-gray-400">{getLabel(item.labelKey)}</div>
                          <div className="text-sm font-semibold text-gray-800">{item.value}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Contract address */}
                {caseItem.contractAddress && (
                  <div className="bg-white rounded-2xl border border-gray-100 p-5">
                    <h4 className="font-bold text-gray-800 text-sm mb-2 flex items-center gap-2">
                      <i className="ri-fingerprint-line text-[#7C3AED]"></i>{getLabel('contractAddress')}
                    </h4>
                    <div className="bg-gray-50 rounded-lg px-4 py-3 flex items-center justify-between">
                      <code className="text-xs text-gray-700 font-mono break-all">{caseItem.contractAddress}</code>
                      <a href={`https://etherscan.io/token/${caseItem.contractAddress}`} target="_blank" rel="nofollow noopener noreferrer"
                        className="ml-3 text-[#7C3AED] text-xs shrink-0 hover:underline cursor-pointer">
                        {getLabel('viewOnEtherscan')}
                      </a>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Tab: Smart Contract */}
            {activeTab === 'contract' && caseItem.contractCode && (
              <div className="bg-[#1A1535] rounded-2xl overflow-hidden border border-[#3D1F8A]/40">
                {/* Contract header */}
                <div className="flex items-center justify-between px-5 py-3.5 border-b border-white/10">
                  <div className="flex items-center gap-3">
                    <div className="flex gap-1.5">
                      <span className="w-3 h-3 rounded-full bg-red-400"></span>
                      <span className="w-3 h-3 rounded-full bg-amber-400"></span>
                      <span className="w-3 h-3 rounded-full bg-emerald-400"></span>
                    </div>
                    <div>
                      <span className="text-white/80 text-sm font-semibold">{getLabel('contractSourceCode')}</span>
                      <span className="text-white/40 text-xs ml-3">Solidity • {caseItem.standard?.includes('ERC') ? caseItem.standard : getLabel('ercStandard')}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {caseItem.contractAddress && (
                      <a href={`https://etherscan.io/token/${caseItem.contractAddress}`} target="_blank" rel="nofollow noopener noreferrer"
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-white/10 text-white/70 text-xs rounded-lg hover:bg-white/20 transition-colors cursor-pointer whitespace-nowrap">
                        <i className="ri-external-link-line text-xs"></i>{getLabel('viewOnEtherscanFull')}
                      </a>
                    )}
                    <button onClick={handleCopyCode}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-[#7C3AED]/70 text-white text-xs rounded-lg hover:bg-[#7C3AED] transition-colors cursor-pointer whitespace-nowrap">
                      <i className={`text-xs ${codeCopied ? 'ri-check-line' : 'ri-file-copy-line'}`}></i>
                      {codeCopied ? getLabel('copied') : getLabel('copyCode')}
                    </button>
                  </div>
                </div>

                {/* Code with line numbers */}
                <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
                  <div className="flex">
                    <div className="select-none px-4 py-5 text-right border-r border-white/5 min-w-[48px]">
                      {caseItem.contractCode.split('\n').map((_, i) => (
                        <div key={i} className="text-white/20 text-xs leading-[22px] font-mono">{i + 1}</div>
                      ))}
                    </div>
                    <pre className="flex-1 px-5 py-5 text-xs leading-[22px] font-mono overflow-x-auto">
                      {caseItem.contractCode.split('\n').map((line, i) => (
                        <div key={i} className={`${highlightSolidity(line)}`}>
                          {line || ' '}
                        </div>
                      ))}
                    </pre>
                  </div>
                </div>

                {/* Contract notice */}
                <div className="px-5 py-3.5 border-t border-white/10 flex items-center gap-2">
                  <i className="ri-information-line text-amber-400 text-sm"></i>
                  <span className="text-white/50 text-xs">{getLabel('contractNotice')}</span>
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            {/* Quick stats */}
            <div className="bg-white rounded-2xl border border-gray-100 p-5">
              <h3 className="font-black text-gray-900 text-sm mb-4">{getLabel('keyData')}</h3>
              <div className="space-y-3">
                {sidebarStats.map((item) => (
                  <div key={item.labelKey} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                    <span className="text-xs text-gray-500 flex items-center gap-1.5">
                      <i className={`${item.icon} text-gray-400`}></i>{getLabel(item.labelKey)}
                    </span>
                    <span className={`text-xs font-bold ${item.color}`}>{item.value}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Related cases */}
            {relatedCases.length > 0 && (
              <div className="bg-white rounded-2xl border border-gray-100 p-5">
                <h3 className="font-black text-gray-900 text-sm mb-4 flex items-center gap-2">
                  <i className="ri-folder-chart-line text-[#7C3AED]"></i>{getLabel('relatedCases')}
                </h3>
                <div className="space-y-3">
                  {relatedCases.map((rc) => (
                    <Link key={rc.id} to={`/cases/${rc.id}`}
                      className="flex items-start gap-2.5 p-3 rounded-xl hover:bg-[#F8F7FF] transition-colors cursor-pointer group block">
                      <div className="w-6 h-6 flex items-center justify-center bg-[#7C3AED]/10 rounded-full text-[#7C3AED] text-xs font-bold shrink-0 mt-0.5">{rc.id}</div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-700 group-hover:text-[#7C3AED] transition-colors line-clamp-2 leading-snug">{rc.name}</p>
                        <p className="text-xs text-gray-400 mt-0.5">{rc.tvl} · {rc.region}</p>
                      </div>
                    </Link>
                  ))}
                </div>
                <Link to="/cases" className="flex items-center justify-center gap-1.5 mt-4 text-xs text-[#7C3AED] font-semibold hover:underline cursor-pointer">
                  {getLabel('viewAllType')} {getTypeLabel(caseItem.type)} {getLabel('cases')} <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            )}

            {/* Back button */}
            <Link to="/cases"
              className="w-full flex items-center justify-center gap-2 py-3 bg-[#7C3AED] text-white rounded-xl font-semibold text-sm hover:bg-[#6D28D9] transition-colors cursor-pointer">
              <i className="ri-arrow-left-line"></i>{getLabel('backToLibBtn')}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function CaseDetailPage() {
  return (
    <LangProvider>
      <CaseDetailContent />
    </LangProvider>
  );
}

// Simple Solidity syntax colorizer
function highlightSolidity(line: string): string {
  if (line.trim().startsWith('//') || line.trim().startsWith('*') || line.trim().startsWith('/**') || line.trim().startsWith('*/')) {
    return 'text-[#6B9E6B]';
  }
  if (/^\s*(pragma|import|contract|interface|library|struct|event|modifier|mapping|function|constructor|emit|require|return|if|else|for|while|uint|uint256|address|bool|string|bytes|bytes32|memory|storage|calldata|public|private|internal|external|view|pure|payable|override|virtual|is|new|delete|revert|assembly)\b/.test(line)) {
    return 'text-[#BD93F9]';
  }
  if (/^\s*\/\//.test(line)) return 'text-[#6B9E6B]';
  if (/".*"/.test(line) || /'.*'/.test(line)) return 'text-[#F1FA8C] text-opacity-90';
  return 'text-[#F8F8F2]';
}
